import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import ImageSelect from '../../components/global/images/ImageSelector';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import SuccessOrErrorDialog from '../../components/global/SuccessOrErrorDialog';
import { getPreviewSiteConfigByCode, updatePreviewSiteConfig } from '../../firebase/firebase-delegate';
import { PREVIEW_SITE_DEFAULT_MAP_EMBEDS, PreviewSiteConfig, PreviewSiteType, createEmptyPreviewSiteConfig } from '../../types';

const GOOGLE_MAPS_EMBED_REGEX = /^https:\/\/www\.google\.com\/maps\/embed\?/i;

const extractGoogleMapsEmbedSrc = (input: string): string => {
  const trimmed = input.trim();
  if (!trimmed) {
    return '';
  }

  const iframeMatch = trimmed.match(/<iframe[^>]*\ssrc=["']([^"']+)["'][^>]*>/i);
  return iframeMatch?.[1]?.trim() ?? trimmed;
};

const validateGoogleMapsEmbedSrc = (input: string): string | null => {
  const normalized = extractGoogleMapsEmbedSrc(input);
  if (!normalized) {
    return 'Google Maps embed is required.';
  }

  if (!GOOGLE_MAPS_EMBED_REGEX.test(normalized)) {
    return 'Enter a valid Google Maps embed URL or paste the full iframe embed code.';
  }

  return null;
};

const isValidHexColor = (value: string): boolean => /^#[0-9A-F]{6}$/i.test(value.trim());

const normalizePreviewSiteConfigForSave = (config: PreviewSiteConfig): PreviewSiteConfig => ({
  ...config,
  heroTitle: config.heroTitle.trim(),
  heroSubtitle: config.heroSubtitle.trim(),
  primaryColor: config.primaryColor.trim().toUpperCase(),
  secondaryColor: config.secondaryColor.trim().toUpperCase(),
  googleMapEmbed: extractGoogleMapsEmbedSrc(config.googleMapEmbed),
});

const getDefaultMapEmbed = (siteType: PreviewSiteType): string => PREVIEW_SITE_DEFAULT_MAP_EMBEDS[siteType];
const getPreviewSiteUrl = (code: string): string => {
  const baseUrl = process.env.REACT_APP_ENV === 'local'
    ? 'http://localhost:5173'
    : 'https://scheduify.com';

  return `${baseUrl}/preview?code=${encodeURIComponent(code)}`;
};

const PreviewSiteConfigPage: React.FC = () => {
  const { code } = useParams();
  const normalizedCode = useMemo(() => (code ?? '').toUpperCase(), [code]);
  const [config, setConfig] = useState<PreviewSiteConfig>(createEmptyPreviewSiteConfig());
  const [loadedConfig, setLoadedConfig] = useState<PreviewSiteConfig | null>(null);
  const [logoFile, setLogoFile] = useState<File | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [saveResult, setSaveResult] = useState<'saved' | 'unchanged'>('saved');
  const [copyStatusMessage, setCopyStatusMessage] = useState<string | null>(null);

  const previewSiteQuery = useQuery({
    queryKey: ['previewSiteConfig', normalizedCode],
    enabled: !!normalizedCode,
    queryFn: async () => getPreviewSiteConfigByCode(normalizedCode),
  });

  useEffect(() => {
    if (previewSiteQuery.data) {
      setLoadedConfig(previewSiteQuery.data);
      setConfig(previewSiteQuery.data);
      return;
    }

    if (previewSiteQuery.data === null) {
      setLoadedConfig(null);
      setConfig(createEmptyPreviewSiteConfig());
    }
  }, [previewSiteQuery.data]);

  const normalizedMapEmbed = useMemo(
    () => extractGoogleMapsEmbedSrc(config.googleMapEmbed),
    [config.googleMapEmbed]
  );

  const mapEmbedError = useMemo(
    () => validateGoogleMapsEmbedSrc(config.googleMapEmbed),
    [config.googleMapEmbed]
  );

  const handleFieldChange = <K extends keyof PreviewSiteConfig>(field: K, value: PreviewSiteConfig[K]) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleColorChange = (field: 'primaryColor' | 'secondaryColor', value: string) => {
    handleFieldChange(field, value.toUpperCase());
  };

  const handleGoogleMapEmbedChange = (value: string) => {
    handleFieldChange('googleMapEmbed', extractGoogleMapsEmbedSrc(value));
  };

  const handleSiteTypeChange = (siteType: PreviewSiteType) => {
    setConfig((prev) => {
      const currentMap = extractGoogleMapsEmbedSrc(prev.googleMapEmbed);
      const isUsingDefaultMap =
        currentMap === getDefaultMapEmbed('barbershop') ||
        currentMap === getDefaultMapEmbed('nailsalon');

      return {
        ...prev,
        siteType,
        googleMapEmbed: isUsingDefaultMap ? createEmptyPreviewSiteConfig(siteType).googleMapEmbed : prev.googleMapEmbed,
      };
    });
  };

  const handleResetMapEmbed = () => {
    handleFieldChange('googleMapEmbed', getDefaultMapEmbed(config.siteType));
  };

  const handleCopyPreviewLink = async () => {
    try {
      await navigator.clipboard.writeText(getPreviewSiteUrl(normalizedCode));
      setCopyStatusMessage('Preview link copied.');
    } catch {
      setCopyStatusMessage('Unable to copy preview link.');
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!normalizedCode) {
        throw new Error('Preview code is missing.');
      }
      if (!/^[A-Z0-9]{1,6}$/.test(normalizedCode)) {
        throw new Error('Preview code must be alphanumeric and 6 characters or fewer.');
      }
      if (!config.heroTitle.trim()) {
        throw new Error('Hero title is required.');
      }
      if (!config.heroSubtitle.trim()) {
        throw new Error('Hero subtitle is required.');
      }
      if (!isValidHexColor(config.primaryColor)) {
        throw new Error('Primary color must be a valid hex value like #000000.');
      }
      if (!isValidHexColor(config.secondaryColor)) {
        throw new Error('Secondary color must be a valid hex value like #000000.');
      }
      if (mapEmbedError) {
        throw new Error(mapEmbedError);
      }
      if (!config.businessLogoPath && !logoFile) {
        throw new Error('Business logo is required.');
      }

      const nextConfig = normalizePreviewSiteConfigForSave(config);
      const baselineConfig = loadedConfig ? normalizePreviewSiteConfigForSave(loadedConfig) : null;

      if (!logoFile && baselineConfig && JSON.stringify(nextConfig) === JSON.stringify(baselineConfig)) {
        return { status: 'unchanged' as const, savedConfig: baselineConfig };
      }

      await updatePreviewSiteConfig(normalizedCode, nextConfig, logoFile);
      return { status: 'saved' as const, savedConfig: nextConfig };
    },
    onSuccess: (result) => {
      setSaveResult(result.status);
      setLoadedConfig(result.savedConfig);
      setConfig(result.savedConfig);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
    },
    onSettled: () => {
      setResultDialogOpen(true);
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    saveMutation.mutate();
  };

  const handleCloseResultDialog = () => {
    setResultDialogOpen(false);
    if (saveMutation.isSuccess) {
      setLogoFile(undefined);
    }
  };

  const commonFieldSx = { width: '100%', maxWidth: 640, mb: 2 };

  if (previewSiteQuery.isLoading) {
    return <LoadingSpinner />;
  }

  if (previewSiteQuery.isError) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Failed to load preview site configuration.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" align="center" gutterBottom fontWeight="bold">
        Preview Site Configuration
      </Typography>
      <Box
        sx={{
          width: '100%',
          maxWidth: 640,
          mx: 'auto',
          mb: 4,
          px: 2,
          py: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="overline" display="block">
          Site Code
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight="bold">
            {normalizedCode}
          </Typography>
          <Tooltip title="Copy preview link">
            <IconButton size="small" onClick={handleCopyPreviewLink} aria-label="Copy preview link">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {copyStatusMessage && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {copyStatusMessage}
          </Typography>
        )}
      </Box>

      <form onSubmit={handleSubmit}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <FormControl sx={commonFieldSx}>
            <FormLabel>Site Type</FormLabel>
            <RadioGroup
              row
              value={config.siteType}
              onChange={(e) => handleSiteTypeChange(e.target.value as PreviewSiteType)}
            >
              <FormControlLabel value="barbershop" control={<Radio />} label="Barbershop" />
              <FormControlLabel value="nailsalon" control={<Radio />} label="Nail Salon" />
            </RadioGroup>
          </FormControl>

          <Typography variant="overline" align="center" display="block">
            BUSINESS LOGO
          </Typography>
          <ImageSelect
            initialImagePath={previewSiteQuery.data?.businessLogoPath}
            allowDelete={false}
            styleType="landscape"
            darkBackground={true}
            onImageChange={(file) => setLogoFile(file)}
            altText="Preview Site Business Logo"
          />

          <TextField
            label="Hero Title"
            value={config.heroTitle}
            onChange={(e) => handleFieldChange('heroTitle', e.target.value)}
            sx={commonFieldSx}
            required
          />
          <TextField
            label="Hero Subtitle"
            value={config.heroSubtitle}
            onChange={(e) => handleFieldChange('heroSubtitle', e.target.value)}
            sx={commonFieldSx}
            required
            multiline
            minRows={2}
          />

          <Box sx={{ ...commonFieldSx, display: 'grid', gap: 2 }}>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <TextField
                label="Primary Color"
                value={config.primaryColor}
                onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                error={!!config.primaryColor && !isValidHexColor(config.primaryColor)}
                helperText="Hex value like #000000"
                sx={{ flex: 1, minWidth: 220 }}
                required
              />
              <Box
                component="input"
                type="color"
                value={isValidHexColor(config.primaryColor) ? config.primaryColor : '#000000'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleColorChange('primaryColor', e.target.value)}
                sx={{ width: 64, height: 56, border: 'none', background: 'transparent', cursor: 'pointer' }}
              />
            </Box>

            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <TextField
                label="Secondary Color"
                value={config.secondaryColor}
                onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                error={!!config.secondaryColor && !isValidHexColor(config.secondaryColor)}
                helperText="Hex value like #000000"
                sx={{ flex: 1, minWidth: 220 }}
                required
              />
              <Box
                component="input"
                type="color"
                value={isValidHexColor(config.secondaryColor) ? config.secondaryColor : '#FFFFFF'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleColorChange('secondaryColor', e.target.value)}
                sx={{ width: 64, height: 56, border: 'none', background: 'transparent', cursor: 'pointer' }}
              />
            </Box>
          </Box>

          <TextField
            label="Google Map Embed"
            value={config.googleMapEmbed}
            onChange={(e) => handleGoogleMapEmbedChange(e.target.value)}
            error={!!mapEmbedError}
            helperText={mapEmbedError ?? 'Paste the Google Maps embed URL or the full iframe embed snippet.'}
            sx={commonFieldSx}
            required
            multiline
            minRows={4}
          />
          <Box sx={{ ...commonFieldSx, mt: -1 }}>
            <Button variant="text" onClick={handleResetMapEmbed} sx={{ px: 0 }}>
              Reset Map
            </Button>
          </Box>

          <Box sx={{ ...commonFieldSx, mt: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Map Preview
            </Typography>
            {mapEmbedError ? (
              <Alert severity="warning">{mapEmbedError}</Alert>
            ) : (
              <Box
                component="iframe"
                src={normalizedMapEmbed}
                height="250px"
                loading="lazy"
                style={{ border: 0 }}
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                sx={{
                  width: { xs: '100%', sm: '100%', md: '50%' },
                  margin: '0 auto',
                  display: 'block',
                }}
              />
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            disabled={saveMutation.isPending}
            sx={{ width: '100%', maxWidth: 640, mt: 2 }}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Preview Site Configuration'}
          </Button>
        </Box>
      </form>

      <SuccessOrErrorDialog
        open={resultDialogOpen}
        onClose={handleCloseResultDialog}
        success={saveMutation.isSuccess}
        successTitle={saveResult === 'saved' ? 'Preview Site Configuration Saved!' : 'No Changes to Save'}
        successMessage={saveResult === 'saved' ? 'The preview site has been updated.' : 'The preview site configuration is unchanged, so nothing was re-saved.'}
        errorTitle="Error Occurred"
        errorMessage={errorMessage ?? 'Something went wrong while saving the preview site.'}
      />
    </Container>
  );
};

export default PreviewSiteConfigPage;
